import {
  AvatarGroup,
  Box,
  Button,
  Center,
  Flex,
  Image,
  QrCode,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import { Avatar, ButtonGray, CopyIcon, tools, GroupIcon } from '@halo/ui-chat-react';
import type { SessionStore } from '@halo/chat-sdk-react';
import { isSessionCategory, SessionCategoryPrefix, useGroupMembers } from '@halo/chat-sdk-react';
import { useQrCodeHelper } from './helpers';

export interface QrCodeDialogData {
  link: string;
  avatar: string;
  name: string;
  username: string;
  source: 'group' | 'user';
  sessionStore: SessionStore;
}

interface QrCodeDialogProps {
  open: boolean;
  setIsOpen: (open: boolean) => void;
  data: QrCodeDialogData | null;
}

const QrCodeDialog = ({ open, setIsOpen, data }: QrCodeDialogProps) => {
  const { t } = useTranslation();
  const { link, avatar, name, username, sessionStore } = data ?? {};
  const [isCopied, setIsCopied] = useState(false);
  const giveWidth = 'calc(var(--text-scale-factor, 0px) + 20px)';
  const { members, run } = useGroupMembers(sessionStore?.session?.chat_id);

  const isGroup = useMemo(() => {
    return isSessionCategory(sessionStore?.session?.chat_id, SessionCategoryPrefix.Group);
  }, [sessionStore?.session?.chat_id]);

  const { qrCodeRef, copyLink, copyQrCode, downloadCanvasQRCode } = useQrCodeHelper(
    link,
    '',
    data?.source ?? 'user',
    t
  );

  useEffect(() => {
    if (!isGroup) {
      return;
    }
    if (open) {
      return run();
    }
  }, [open, run, isGroup]);

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={(details) => setIsOpen(details.open)}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content borderRadius="xl" w="424px" maxW="100%">
          <Dialog.Header px="5" pt="4" pb="0">
            <Text py="2.5" fontSize="md" fontWeight="semibold">
              {t('chat.qrCode')}
            </Text>
          </Dialog.Header>
          <Dialog.Body px="5" pt="0">
            <Stack gap="0" justifyContent="center">
              <Stack className="QrCode" gap="4" pt="4.5" pb="4" ref={qrCodeRef}>
                <Flex
                  fontSize="sm"
                  direction={isGroup ? 'column' : 'row'}
                  gap="1.5"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Avatar src={avatar} size="xl" activeShake={false} />
                  <Flex
                    w={isGroup ? 'full' : 'auto'}
                    overflow="hidden"
                    fontSize="md"
                    fontWeight="semibold"
                    justifyContent="center"
                    alignItems="center"
                    gap="0.5"
                  >
                    {isGroup && (
                      <Center flexShrink="0">
                        <GroupIcon />
                      </Center>
                    )}
                    <Box truncate>{name}</Box>
                  </Flex>
                  {members?.length && (
                    <Flex alignItems="center" gap="0.5" justifyContent="center">
                      <AvatarGroup spaceX="-1" size="sm">
                        {members.slice(0, 3).map((member) => (
                          <Avatar
                            activeShake={false}
                            giveWidth={giveWidth}
                            src={member.avatar}
                            key={member.user_id}
                          />
                        ))}
                      </AvatarGroup>
                      <Box
                        fontSize="xs"
                        fontWeight="semibold"
                        flex="0 0 auto"
                        whiteSpace="nowrap"
                      >
                        {members.length}
                      </Box>
                    </Flex>
                  )}
                </Flex>
                <Box>
                  <Flex justifyContent="center" alignItems="center">
                    <Box w="260px" h="260px">
                      <QrCode.Root value={link} size="full" encoding={{ ecc: 'H' }}>
                        <QrCode.Frame style={{ fill: 'url(#QrCodeBg)' }}>
                          <defs>
                            <linearGradient
                              id="QrCodeBg"
                              x1="130.5"
                              y1="260.5"
                              x2="130.5"
                              y2="0.5"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop
                                stopColor="#7750E5"
                                style={{ stopColor: '#7750E5', stopOpacity: 1 }}
                              ></stop>
                              <stop
                                offset="1"
                                stopColor="#B54EE2"
                                style={{ stopColor: '#B54EE2', stopOpacity: 1 }}
                              ></stop>
                            </linearGradient>
                          </defs>
                          <QrCode.Pattern />
                        </QrCode.Frame>
                        <QrCode.Overlay borderRadius="full">
                          <Image
                            src="/favicon.png"
                            borderRadius="full"
                            alt="logo"
                            w="full"
                            h="full"
                          />
                        </QrCode.Overlay>
                      </QrCode.Root>
                    </Box>
                  </Flex>
                  <Box
                    fontSize="xl"
                    fontWeight="bold"
                    pt="5"
                    px="5"
                    textAlign="center"
                    truncate
                    bg="linear-gradient(279deg, #00A2FF -2.17%, #7824FF 64.56%, #CF1EFF 94.42%)"
                    backgroundClip="text"
                    margin="0 auto"
                    maxW="full"
                    w="fit-content"
                    color="transparent"
                    lineHeight="initial"
                  >
                    @{username ?? ''}
                  </Box>
                </Box>
              </Stack>
              <Flex
                bg="bg.subtle"
                rounded="lg"
                py="3"
                px="4"
                justifyContent="space-between"
                alignItems="center"
                gap="2.5"
              >
                <Box flex="1" color="blue.100" truncate>
                  {tools.getShareUrlEllipsis(link!, 34)}
                </Box>
                <Flex
                  alignItems="center"
                  gap="2"
                  cursor="pointer"
                  onClick={async () => {
                    setIsCopied(true);
                    await copyLink(name);
                    const timer = setTimeout(() => {
                      setIsCopied(false);
                      clearTimeout(timer);
                    }, 1000);
                  }}
                  fontSize="sm"
                  bg="bg"
                  rounded="full"
                  p="2"
                  px="3"
                >
                  <CopyIcon />
                  <Text fontWeight="semibold">
                    {isCopied ? t('tips.copied') : t('tips.copyLink')}
                  </Text>
                </Flex>
              </Flex>
              {/* <Flex pt="5" gap="0.5" alignItems="center" justifyContent="center">
                <Box w="9" h="9">
                  <img src="/favicon.png" alt="logo" />
                </Box>
                <Box>
                  <Text fontSize="md" fontWeight="semibold">
                    HaloChat
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                    Encrypt all, keep privacy
                  </Text>
                </Box>
              </Flex> */}
            </Stack>
          </Dialog.Body>
          <Dialog.Footer gap="4">
            <ButtonGray
              minW="96px"
              h="10"
              borderRadius="xl"
              onClick={() => downloadCanvasQRCode()}
            >
              {t('buttons.saveImage')}
            </ButtonGray>
            <Button minW="96px" h="10" borderRadius="xl" onClick={() => copyQrCode()}>
              {t('buttons.copyImage')}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

const MemoizedQrCodeDialog = memo(QrCodeDialog);

export { MemoizedQrCodeDialog as QrCodeDialog };
